import { Flex } from "@chakra-ui/react";
import { CommonTooltip } from "@root/components/common/tooltip";
import { useEffect } from "react";

type ToggleOption = {
  label: string;
  type: number; // value passed to setReview()
  key?: string; // unique key for active state
};

type Props = {
  options: ToggleOption[];
  active: number;
  setActive: (key: number) => void;
  setState: (count: any) => void;
  disabled?: boolean;
  tooltipLabel?: string;
  variant?: "primary" | "secondary";
  onCustomChange?: (newState: number) => void;
};

export default function PillToggle({
  options,
  active,
  setActive,
  setState,
  disabled = false,
  tooltipLabel,
  variant = "primary",
  onCustomChange,
}: Props) {
  // Size configurations for different variants
  const sizes = {
    primary: {
      outerPadding: "4px",
      outerGap: "4px",
      outerBorderRadius: "50px",
      innerHeight: "44px",
      innerPx: "16px",
      innerPy: "6px",
      innerGap: "6px",
      innerBorderRadius: "40px",
      fontSize: "14px",
    },
    secondary: {
      outerPadding: "4px",
      outerGap: "4px",
      outerBorderRadius: "30px",
      innerHeight: "36px",
      innerPx: "12px",
      innerPy: "6px",
      innerGap: "4px",
      innerBorderRadius: "24px",
      fontSize: "14px",
    },
  };

  const currentSizes = sizes[variant];

  const toggleContent = (
    <Flex
      bg="#ECEFF3"
      p={currentSizes.outerPadding}
      borderRadius={currentSizes.outerBorderRadius}
      align="center"
      gap={currentSizes.outerGap}
      width="fit-content"
      opacity={disabled ? 0.5 : 1}
      transition="opacity 0.2s ease"
    >
      {options?.map((opt, index) => {
        const isActive = active === opt?.type;

        return (
          <Flex
            key={opt?.key || opt?.type || index}
            onClick={() => {
              if (!disabled) {
                setActive(opt?.type); // update UI
                setState(opt?.type); // send number back
                onCustomChange?.(opt?.type); // call custom change handler with new state
              }
            }}
            cursor={disabled ? "not-allowed" : "pointer"}
            height={currentSizes.innerHeight}
            px={currentSizes.innerPx}
            py={currentSizes.innerPy}
            borderRadius={currentSizes.innerBorderRadius}
            align="center"
            gap={currentSizes.innerGap}
            bg={isActive ? "white" : "transparent"}
            color={isActive ? "#6648FF" : "#6E737A"}
            fontWeight="500"
            fontSize={currentSizes.fontSize}
            transition="all 0.2s ease"
            _hover={
              disabled
                ? {}
                : {
                    bg: isActive ? "white" : "rgba(255, 255, 255, 0.5)",
                  }
            }
          >
            <span>{opt?.label}</span>
          </Flex>
        );
      })}
    </Flex>
  );

  if (disabled && tooltipLabel) {
    return <CommonTooltip label={tooltipLabel}>{toggleContent}</CommonTooltip>;
  }

  return toggleContent;
}
